import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Eye, Trash2, Send, ScanEye } from "lucide-react";

const POLL_MS = 30000;

/**
 * Fase 10 — publish bar shown in the admin header.
 * - Green: menu published, no pending changes.
 * - Yellow: pending edits with count + [Anteprima] [Pubblica] [Scarta].
 *
 * Refresh strategy:
 *  - Poll `/admin/menu/status` every 30s as fallback.
 *  - Refresh IMMEDIATELY when the global `sb-menu-changed` event is dispatched
 *    (fired by api.js on every write to /admin/{categories,products,menus,modifier-groups}).
 */
export default function MenuPublishBar({ canPublish }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [notes, setNotes] = useState("");

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/admin/menu/status");
      setStatus(r.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, POLL_MS);
    const onChange = () => refresh();
    window.addEventListener("sb-menu-changed", onChange);
    return () => { clearInterval(t); window.removeEventListener("sb-menu-changed", onChange); };
  }, [refresh]);

  const doPublish = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/menu/publish", { notes: notes.trim() || null });
      toast.success(`Menu pubblicato · v.${r.data.version}`);
      setPublishOpen(false); setNotes("");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const doDiscard = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/menu/discard");
      toast.success(`Modifiche scartate · ripristinata v.${r.data.restored_to_version}`);
      setDiscardOpen(false);
      window.dispatchEvent(new CustomEvent("sb-menu-changed"));
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!status) return null;

  const pending = status.has_pending_changes;
  return (
    <div data-testid="menu-publish-bar"
         className={`sticky top-0 z-30 border-b px-4 py-2 flex items-center gap-3 flex-wrap text-sm
                    ${pending ? "bg-amber-50 border-amber-200 text-amber-900"
                              : "bg-emerald-50 border-emerald-200 text-emerald-900"}`}>
      {pending ? (
        <>
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span data-testid="menu-publish-status" className="font-medium">
            Hai {status.total_changes} modific{status.total_changes === 1 ? "a" : "he"} non pubblicat{status.total_changes === 1 ? "a" : "e"}
          </span>
          <span className="text-xs text-amber-700">
            categorie {status.counts.categories} · prodotti {status.counts.products} · modificatori {status.counts.modifier_groups} · menu {status.counts.menus}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <a href="/preview-menu" target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-1 rounded-full border border-amber-400 bg-white/60 px-3 py-1 text-xs hover:bg-white"
               data-testid="menu-publish-preview">
              <Eye className="w-3 h-3" /> Anteprima
            </a>
            <Link to="/admin/menu/diff"
                  className="inline-flex items-center gap-1 rounded-full border border-amber-400 bg-white/60 px-3 py-1 text-xs hover:bg-white"
                  data-testid="menu-diff-btn">
              <ScanEye className="w-3 h-3" /> Anteprima modifiche
            </Link>
            {canPublish && (
              <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700"
                          data-testid="menu-publish-btn">
                    <Send className="w-3 h-3 mr-1" /> Pubblica
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader><DialogTitle>Pubblica {status.total_changes} modific{status.total_changes === 1 ? "a" : "he"}?</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <p className="text-sm text-ink-500">
                      Il menu cliente verrà aggiornato immediatamente. La versione attuale (v.{status.version}) resterà archiviata per un eventuale rollback futuro.
                    </p>
                    <div>
                      <label className="text-xs font-medium">Nota (opzionale)</label>
                      <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                                placeholder="es. Menu invernale, prezzi ritoccati…"
                                data-testid="menu-publish-notes" rows={3} />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setPublishOpen(false)}>Annulla</Button>
                    <Button onClick={doPublish} disabled={busy}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            data-testid="menu-publish-confirm">
                      {busy ? "Pubblico…" : "Pubblica ora"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
            {canPublish && (
              <Dialog open={discardOpen} onOpenChange={setDiscardOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" className="rounded-full text-red-700 border-red-300"
                          data-testid="menu-discard-btn">
                    <Trash2 className="w-3 h-3 mr-1" /> Scarta
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader><DialogTitle>Scarta le modifiche non pubblicate?</DialogTitle></DialogHeader>
                  <p className="text-sm text-ink-500">
                    L'operazione ripristina il menu allo stato pubblicato (v.{status.version}).<br/>
                    <b>Le nuove categorie/prodotti/modificatori creati dopo l'ultima pubblicazione verranno persi.</b> Quest'azione non è annullabile.
                  </p>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setDiscardOpen(false)}>Annulla</Button>
                    <Button onClick={doDiscard} disabled={busy}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="menu-discard-confirm">
                      {busy ? "Ripristino…" : "Scarta e ripristina"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </>
      ) : (
        <>
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          <span data-testid="menu-publish-status" className="font-medium">
            Menu pubblicato · v.{status.version}
          </span>
          <span className="text-xs text-emerald-700">
            {status.published_by && <>da {status.published_by} · </>}
            {status.published_at?.slice(0, 16).replace("T", " ")}
          </span>
          <a href="/preview-menu" target="_blank" rel="noopener noreferrer"
             className="ml-auto inline-flex items-center gap-1 rounded-full border border-emerald-400 bg-white/60 px-3 py-1 text-xs hover:bg-white"
             data-testid="menu-publish-preview">
            <Eye className="w-3 h-3" /> Vedi menu cliente
          </a>
        </>
      )}
    </div>
  );
}
